import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/lib/auth';
import { projectsApi } from '@/lib/api';
import { CreateProjectData } from '@/types';
import toast from 'react-hot-toast';

export default function NewProject() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<CreateProjectData>();

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  const createMutation = useMutation(projectsApi.create, {
    onSuccess: (response) => {
      queryClient.invalidateQueries('projects');
      toast.success('Project created successfully!');
      navigate(`/projects/${response.data?.id}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create project');
    },
  });

  const onSubmit = async (data: CreateProjectData) => {
    setIsSubmitting(true);
    try {
      await createMutation.mutateAsync(data);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (authLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
      <div className="px-4 sm:px-0">
        <div className="mb-8">
          <Link
            to="/projects"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Projects
          </Link>
        </div>

        <div className="max-w-2xl">
          <div className="mb-8">
            <h1 className="text-2xl font-semibold leading-6 text-gray-900">
              Create New Project
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              Set up a new PostgreSQL database project with dedicated resources.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="card">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Project Details</h2>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="form-label">
                    Project Name *
                  </label>
                  <input
                    {...register('name', {
                      required: 'Project name is required',
                      minLength: {
                        value: 2,
                        message: 'Project name must be at least 2 characters'
                      }
                    })}
                    type="text"
                    className="form-input"
                    placeholder="My Awesome Project"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="description" className="form-label">
                    Description
                  </label>
                  <textarea
                    {...register('description')}
                    rows={3}
                    className="form-input"
                    placeholder="Describe what this project is for..."
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Optional description to help you remember what this project is for.
                  </p>
                </div>
              </div>
            </div>

            <div className="card">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Database Configuration</h2>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="extensions" className="form-label">
                    PostgreSQL Extensions
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { id: 'uuid-ossp', name: 'UUID OSSP', description: 'UUID generation functions' },
                      { id: 'pgcrypto', name: 'PG Crypto', description: 'Cryptographic functions' },
                      { id: 'postgis', name: 'PostGIS', description: 'Geographic objects support' },
                      { id: 'pg_trgm', name: 'PG Trigram', description: 'Text similarity matching' },
                    ].map((ext) => (
                      <label key={ext.id} className="relative flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            {...register('extensions')}
                            type="checkbox"
                            value={ext.id}
                            className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <span className="font-medium text-gray-700">{ext.name}</span>
                          <p className="text-gray-500">{ext.description}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label htmlFor="initialSchema" className="form-label">
                    Initial Schema (Optional)
                  </label>
                  <textarea
                    {...register('initialSchema')}
                    rows={6}
                    className="form-input font-mono text-sm"
                    placeholder="CREATE TABLE users (
  id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
  email VARCHAR(255) UNIQUE NOT NULL,
  created_at TIMESTAMP DEFAULT NOW()
);"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    SQL commands to run when the database is created.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-4">
              <Link to="/projects" className="btn-secondary">
                Cancel
              </Link>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating...
                  </div>
                ) : (
                  'Create Project'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
  );
}